import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { money, currentPeriod, recentPeriods } from '../lib/format'
import { Panel, Alert, Loading, Empty } from '../components/ui'
import { StackedColumnChart, StackedBar, HeatTable, type HeatRow } from '../components/charts'
import MonthRangePicker from '../components/MonthRangePicker'

interface DashboardData {
  period: string
  monthly: { orders: number; qty: number; amount: number }
  pending_review: number
  overdue: Array<{ code: string; due_on: string; writer_name: string; total_qty: number; days_late: number }>
  writer_load: Array<{
    writer_name: string
    name_en: string | null
    monthly_max: number | null
    period: string
    qty: number
  }>
  expiring_contracts: Array<{ name: string; name_en: string; expires_on: string }>
  billing: Array<{ status: string; n: number; qty: number; amount: number }>
  owner_load: Array<{ owner_name: string; period: string; qty: number }>
  by_lang: Array<{ lang: string; orders: number; qty: number; amount: number }>
  trend: Array<{ period: string; lang: string; qty: number; amount: number }>
}

const BILLING_COLORS: Record<string, string> = {
  已請款: 'var(--chart-paid)',
  待請款: 'var(--chart-alert)',
}

const LANG_COLORS: Record<string, string> = {
  中: 'var(--chart-zh)',
  英: 'var(--chart-en)',
}
const LANG_LABELS: Record<string, string> = { 中: '中文', 英: '英文', 未指定: '未指定' }
/** 堆疊順序固定，語系數量變動時不會整批換色 */
const LANG_ORDER = ['中', '英', '未指定']

/** 負責人發包量的期別粒度，以及各自要往回看幾期 */
const GRANULARITY = [
  { key: '月' as const, span: 6 },
  { key: '季' as const, span: 4 },
  { key: '年' as const, span: 3 },
]
type Gran = (typeof GRANULARITY)[number]['key']

/** 把 YYYY-MM 收斂成該粒度的期別代號 */
function bucketOf(period: string, gran: Gran): string {
  const [y, m] = period.split('-').map(Number)
  if (gran === '月') return period
  if (gran === '季') return `${y} Q${Math.ceil(m / 3)}`
  return String(y)
}

/** 表頭用的短標。月與季省掉世紀（26-03、25 Q4），年度保留完整年份免得看成月份 */
function bucketLabel(bucket: string, gran: Gran): string {
  return gran === '年' ? bucket : bucket.replace(/^20/, '')
}

/** 走勢圖看得比表格長：格子細，同樣寬度塞得下更多期 */
const TREND_SPAN: Record<Gran, number> = { 月: 12, 季: 8, 年: 3 }

/** 該粒度下要顯示的期別清單，由舊到新 */
function bucketsFor(gran: Gran, span = GRANULARITY.find((g) => g.key === gran)!.span): string[] {
  return [...new Set(recentPeriods(36).map((p) => bucketOf(p, gran)))].slice(-span)
}

/** 走勢圖 X 軸的短標。軸上位置有限，能省的字都省掉 */
function trendLabel(bucket: string, gran: Gran): string {
  if (gran === '月') return bucket.slice(5)
  if (gran === '季') return bucket.replace(/^20/, '').replace(' ', '')
  return bucket
}

/** 月／季／年切換鈕。每張表各自持有狀態，互不連動 */
function GranChips({ value, onChange }: { value: Gran; onChange: (g: Gran) => void }) {
  return (
    <span className="chip-row">
      {GRANULARITY.map((g) => (
        <button
          key={g.key}
          className={`chip${value === g.key ? ' on' : ''}`}
          onClick={() => onChange(g.key)}
        >
          {g.key}
        </button>
      ))}
    </span>
  )
}

/** 把「名字 → 期別 → 數量」的明細收斂成熱力表的列，總量高的排前面 */
function toHeatRows<T>(
  source: T[],
  buckets: string[],
  gran: Gran,
  pick: (r: T) => { name: string; sub?: string; period: string; qty: number }
) {
  const byName = new Map<string, { name: string; sub?: string; cells: Record<string, number> }>()
  for (const raw of source) {
    const r = pick(raw)
    const b = bucketOf(r.period, gran)
    if (!buckets.includes(b)) continue
    const row = byName.get(r.name) ?? { name: r.name, sub: r.sub, cells: {} }
    row.cells[b] = (row.cells[b] ?? 0) + r.qty
    byName.set(r.name, row)
  }
  return [...byName.values()]
    .map((r) => ({ ...r, total: buckets.reduce((s, b) => s + (r.cells[b] ?? 0), 0) }))
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total)
}

export default function Dashboard() {
  const [from, setFrom] = useState(currentPeriod())
  const [to, setTo] = useState(currentPeriod())
  /**
   * 跨月時把「本月」字樣換成區間，免得數字與標題對不上。
   * 區間字串後面補一個空格，否則「2026-06 ～ 2026-08發包單」會黏在一起。
   */
  const scope = from === to ? '本月' : `${from} ～ ${to} `
  const [data, setData] = useState<DashboardData | null>(null)
  const [error, setError] = useState('')
  // 走勢圖與兩張熱力表各自記住自己的粒度，切換互不干擾
  const [trendGran, setTrendGran] = useState<Gran>('月')
  const [ownerGran, setOwnerGran] = useState<Gran>('月')
  const [writerGran, setWriterGran] = useState<Gran>('月')

  useEffect(() => {
    setData(null)
    api
      .get<DashboardData>(`/dashboard?from=${from}&to=${to}`)
      .then(setData)
      .catch((e) => setError(e.message))
  }, [from, to])

  const pending = data?.billing.find((b) => b.status === '待請款')

  // API 回的是「月 × 語系」的明細。這裡收斂成選定粒度的期別，
  // 沒有發包的期別也保留成空格子，X 軸才會連續。
  const trendBuckets = bucketsFor(trendGran, TREND_SPAN[trendGran])
  const trend = trendBuckets.map((bucket) => {
    const rows = (data?.trend ?? []).filter((t) => bucketOf(t.period, trendGran) === bucket)
    const byLang: Record<string, number> = {}
    const byLangAmount: Record<string, number> = {}
    for (const r of rows) {
      byLang[r.lang] = (byLang[r.lang] ?? 0) + r.qty
      // 長條的高度用千元，金額直接用「元」會讓座標軸刻度長到擠不下。
      // 這裡不先取整——各語系各自四捨五入再相加，堆疊總高會跟實際金額對不起來
      byLangAmount[r.lang] = (byLangAmount[r.lang] ?? 0) + r.amount / 1000
    }
    return {
      bucket,
      label: trendLabel(bucket, trendGran),
      amount: rows.reduce((s, r) => s + r.amount, 0),
      qty: rows.reduce((s, r) => s + r.qty, 0),
      byLang,
      byLangAmount,
    }
  })
  const hasTrend = trend.some((t) => t.amount > 0 || t.qty > 0)
  const trendRange = `${trendBuckets[0]} ～ ${trendBuckets[trendBuckets.length - 1]}`

  // 只畫實際出現過的語系，避免圖例列出一個從沒用到的「未指定」
  const trendLangs = LANG_ORDER.filter((k) => trend.some((t) => (t.byLang[k] ?? 0) > 0))
  const trendSeries = trendLangs.map((k) => ({
    key: k,
    label: LANG_LABELS[k] ?? k,
    color: LANG_COLORS[k] ?? 'var(--chart-na)',
  }))

  const ownerBuckets = bucketsFor(ownerGran)
  const ownerRows = toHeatRows(data?.owner_load ?? [], ownerBuckets, ownerGran, (r) => ({
    name: r.owner_name,
    period: r.period,
    qty: r.qty,
  }))

  // 名冊上限是「每月」的規範，只有月粒度才拿來判斷超標
  const writerBuckets = bucketsFor(writerGran)
  const writerMax = new Map((data?.writer_load ?? []).map((r) => [r.writer_name, r.monthly_max]))
  const writerRows: HeatRow[] = toHeatRows(data?.writer_load ?? [], writerBuckets, writerGran, (r) => ({
    name: r.writer_name,
    sub: r.name_en ?? undefined,
    period: r.period,
    qty: r.qty,
  })).map((r) => ({
    ...r,
    over: (b: string) => {
      const limit = writerMax.get(r.name)
      return writerGran === '月' && limit !== null && limit !== undefined && (r.cells[b] ?? 0) > limit
    },
  }))

  const billingSegments = (data?.billing ?? [])
    .filter((b) => b.amount > 0)
    .map((b) => ({ label: b.status, value: b.amount, color: BILLING_COLORS[b.status] ?? 'var(--chart-ink-soft)' }))

  return (
    <>
      <div className="page-head">
        <h2>儀表板</h2>
        <span className="hint">月份</span>
        <MonthRangePicker from={from} to={to} onChange={(a, b) => { setFrom(a); setTo(b) }} />
      </div>

      <Alert kind="error">{error}</Alert>

      {!data ? (
        <Loading />
      ) : (
        <>
          <div className="stat-row" style={{ marginBottom: 16 }}>
            <div className="stat">
              <div className="label">{scope}發包單</div>
              <div className="value">
                {data.monthly.orders}
                <span className="unit">張</span>
              </div>
            </div>
            <div className="stat">
              <div className="label">{scope}發包篇數</div>
              <div className="value">
                {data.monthly.qty}
                <span className="unit">篇</span>
              </div>
            </div>
            <div className="stat">
              <div className="label">{scope}發包金額</div>
              <div className="value">
                {money(data.monthly.amount)}
                <span className="unit">元</span>
              </div>
            </div>
            <div className="stat">
              <div className="label">待請款</div>
              <div className="value">
                {money(pending?.amount ?? 0)}
                <span className="unit">元 / {pending?.qty ?? 0} 篇</span>
              </div>
            </div>
          </div>

          {data.overdue.length > 0 && (
            <Panel title={`逾期未交稿（${data.overdue.length} 張）`}>
              <table className="data">
                <thead>
                  <tr>
                    <th>發包單號</th>
                    <th>寫手</th>
                    <th>約定交件日</th>
                    <th className="num">逾期天數</th>
                    <th className="num">篇數</th>
                  </tr>
                </thead>
                <tbody>
                  {data.overdue.map((o) => (
                    <tr key={o.code}>
                      <td>{o.code}</td>
                      <td>{o.writer_name}</td>
                      <td>{o.due_on}</td>
                      <td className="num" style={{ color: 'var(--bad)' }}>
                        {o.days_late}
                      </td>
                      <td className="num">{o.total_qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="hint" style={{ marginBottom: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
                依發包單規範，逾交稿日 5 個工作日內未交付，發包方得終止本單並不予計酬。
              </p>
            </Panel>
          )}

          {/* 金額與篇數左右並排，各自有自己的 Y 軸刻度。
              合成一張雙軸圖的話，兩者的相對高低純粹由刻度決定，會看出假的相關性 */}
          {!hasTrend ? (
            <Panel title="發包趨勢" actions={<GranChips value={trendGran} onChange={setTrendGran} />}>
              <Empty>{trendRange} 之間還沒有發包資料</Empty>
            </Panel>
          ) : (
            <div className="grid c2">
              {/* 金額與篇數合成一張：長條高度＝金額（依語系堆疊），頂端小字＝篇數。
                  篇數不另開一條 Y 軸——雙軸的刻度可以各自任意設定，
                  兩者的相對高低會變成純粹由刻度決定的假訊號 */}
              <Panel
                title={`發包趨勢（${trendRange}）`}
                actions={<GranChips value={trendGran} onChange={setTrendGran} />}
              >
                <StackedColumnChart
                  ariaLabel={`每${trendGran}發包金額長條圖，依中英文語系堆疊，長條上方標註篇數`}
                  axisLabel="千元"
                  showXLabels
                  height={170}
                  series={trendSeries}
                  line={{
                    label: `每${trendGran}金額`,
                    values: trend.map((t) => (t.amount > 0 ? t.amount / 1000 : null)),
                  }}
                  topLabel={(i) => (trend[i].qty > 0 ? String(trend[i].qty) : null)}
                  format={(n) => money(n)}
                  data={trend.map((t) => ({
                    label: t.label,
                    values: t.byLangAmount,
                    hint: `${t.bucket}：${money(t.amount)} 元、共 ${t.qty} 篇（${trendSeries
                      .map(
                        (s) =>
                          `${s.label} ${money(Math.round(t.byLangAmount[s.key] ?? 0))} 千元／${t.byLang[s.key] ?? 0} 篇`
                      )
                      .join('；')}）`,
                  }))}
                />
                <p className="chart-note">
                  長條高度＝發包金額，上方小字＝篇數。不隨上方月份區間變動。
                </p>
              </Panel>

              <Panel
                title="案件負責人發包量"
                actions={<GranChips value={ownerGran} onChange={setOwnerGran} />}
              >
                {ownerRows.length === 0 ? (
                  <Empty>近期沒有發包紀錄</Empty>
                ) : (
                  <>
                    <HeatTable
                      head="負責人"
                      buckets={ownerBuckets}
                      bucketLabel={(b) => bucketLabel(b, ownerGran)}
                      rows={ownerRows}
                    />
                    <p className="chart-note">
                      單位：篇，底色深淺＝篇數多寡（整張表共用一把尺，合計列不上色）。
                      負責人以發包明細上的為準，明細沒填才取表頭。不隨上方月份區間變動。
                    </p>
                  </>
                )}
              </Panel>
            </div>
          )}

          {billingSegments.length > 0 && (
            <Panel title={`${scope}請款狀態`}>
              <StackedBar data={billingSegments} format={(n) => `${money(n)} 元`} />
            </Panel>
          )}

          <div className="grid c2">
            <Panel
              title={`寫手稿量（待驗收 ${data.pending_review} 張）`}
              actions={<GranChips value={writerGran} onChange={setWriterGran} />}
            >
              {writerRows.length === 0 ? (
                <Empty>近期沒有發包給任何寫手</Empty>
              ) : (
                <>
                  <HeatTable
                    head="寫手"
                    buckets={writerBuckets}
                    bucketLabel={(b) => bucketLabel(b, writerGran)}
                    rows={writerRows}
                  />
                  <p className="chart-note">
                    單位：篇，底色深淺＝篇數多寡。
                    {writerGran === '月' ? (
                      <>
                        <span style={{ color: 'var(--bad)' }}> ▲</span> 表示超出名冊月上限。
                      </>
                    ) : (
                      '名冊上限是每月的規範，切到月份才會標示超標。'
                    )}
                  </p>
                </>
              )}
            </Panel>

            <Panel title="90 天內到期的寫手合約">
              {data.expiring_contracts.length === 0 ? (
                <Empty>近期沒有合約到期</Empty>
              ) : (
                <table className="data">
                  <thead>
                    <tr>
                      <th>寫手</th>
                      <th>到期日</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expiring_contracts.map((c) => (
                      <tr key={c.name}>
                        <td>
                          {c.name} {c.name_en && <span style={{ color: 'var(--ink-soft)' }}>{c.name_en}</span>}
                        </td>
                        <td>{c.expires_on}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>
        </>
      )}
    </>
  )
}
